import './promoted-products.component.scss';

import type { FunctionComponent } from 'preact';
import { useEffect, useMemo } from 'preact/hooks';
import { Provider } from 'react-redux';

import { LoginType } from '../../../auth/models/auth.model';
import { selectAccountSettings, selectIsAuthenticated } from '../../../auth/store/auth.selectors';
import { checkAuthentication, loadAccountSettings } from '../../../auth/store/auth.thunk';
import type { AccountSettingsResponse } from '../../../models/common.model';
import type { NeedAccessResponse } from '../../../need-access/need-access.interface';
import { AccessResponse, NeedAccessContentType } from '../../../need-access/need-access.interface';
import { store } from '../../../store';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import {
  selectNeedAccess,
  selectNeedAccessLoaded,
  selectPromotedProducts,
  selectPromotedProductsLoading,
} from '../../store/content.selectors';
import { loadNeedAccess, loadPromotedProducts } from '../../store/content.thunk';
import { ProductCard } from '../product-card/product-card.component';

export interface PromotedProductsComponentProps {
  display: 'always' | 'has_access' | 'no_access';
  loggedIn: boolean;
  titleText: string;
  titleSize: string;
  description: string;
  columnCount: string;
  maxWidth: boolean;
  productTitleSize: string;
  showProductTitle: boolean;
  showProductPrice: boolean;
  showQuickAdd: boolean;
  separateTab: boolean;
  collectionHandle: string;
  contentId: string;
  // CSS Variables
  sectionBackground: string;
  textColor: string;
}

const PromotedProductsContent: FunctionComponent<PromotedProductsComponentProps> = ({
  display,
  loggedIn,
  titleText,
  titleSize,
  description,
  maxWidth,
  productTitleSize,
  showProductTitle,
  showProductPrice,
  showQuickAdd,
  separateTab,
  collectionHandle,
  contentId,
  columnCount,
  sectionBackground,
  textColor,
}) => {
  const dispatch = useAppDispatch();
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const loading: boolean = useAppSelector(selectPromotedProductsLoading);
  const promoProductsResp = useAppSelector(selectPromotedProducts);
  const needAccessResponse: NeedAccessResponse = useAppSelector(selectNeedAccess);
  const needAccessLoaded: boolean = useAppSelector(selectNeedAccessLoaded);
  const products =
    promoProductsResp?.productPerks?.map((p) => p.product)?.filter((p) => p && !p.error) ?? [];
  const accountSettingsResp: AccountSettingsResponse = useAppSelector(selectAccountSettings);
  const { accountSettings } = accountSettingsResp || {};

  const id = useMemo(() => {
    const fromProp = (contentId ?? '').trim();
    if (fromProp) return fromProp;

    const params = new URLSearchParams(window.location.search);
    return (params.get('content-id') ?? '').trim();
  }, [contentId, window.location.search]);

  useEffect(() => {
    dispatch(checkAuthentication({ loginTypes: [LoginType.CUSTOMER] }));
    dispatch(loadPromotedProducts({ id, collectionHandle }));
    dispatch(loadAccountSettings());
    if (id) {
      dispatch(loadNeedAccess({ contentId: id, contentType: NeedAccessContentType.VIDEO }));
    }
  }, [dispatch]);

  const cssVars = useMemo(() => {
    const vars: Record<string, string> = {
      '--columnCount': columnCount ?? '',
      '--sectionBackground': sectionBackground ?? '',
      '--textColor': textColor ?? '',
    };

    if (accountSettings?.cardBackgroundColor) {
      vars['--single-settings-bg-color'] = accountSettings.cardBackgroundColor;
    }
    if (accountSettings?.cardBorderColor) {
      vars['--single-settings-brdr-color'] = accountSettings.cardBorderColor;
    }
    if (accountSettings?.cardTextColor) {
      vars['--single-settings-text-color'] = accountSettings.cardTextColor;
    }
    if (accountSettings?.cardButtonBackgroundColor) {
      vars['--single-settings-button-bg-color'] = accountSettings.cardButtonBackgroundColor;
    }
    if (accountSettings?.cardButtonTextColor) {
      vars['--single-settings-button-text-color'] = accountSettings.cardButtonTextColor;
    }
    if (accountSettings?.borderWidth) {
      vars['--single-settings-border-width'] = `${accountSettings.borderWidth}px`;
    }
    if (accountSettings?.borderRadius) {
      vars['--single-settings-border-radius'] = `${accountSettings.borderRadius}px`;
    }

    return vars;
  }, [
    textColor,
    sectionBackground,
    columnCount,
    accountSettings?.cardBackgroundColor,
    accountSettings?.cardBorderColor,
    accountSettings?.cardTextColor,
    accountSettings?.cardButtonBackgroundColor,
    accountSettings?.cardButtonTextColor,
    accountSettings?.borderWidth,
    accountSettings?.borderRadius,
  ]);

  const shouldDisplay = (): boolean => {
    // Logged-out visitors never see promoted products, whatever the block's
    // display setting — on a gated page they'd sit under the locked hero (SNGL-7297)
    if (!isAuthenticated) return false;

    if (!display || display === 'always' || display === 'has_access') {
      return true;
    }

    // 'no_access': logged in but without access to this content. Wait for the
    // need-access result so viewers who do have access don't get a flash.
    if (!id || !needAccessLoaded) return false;
    return needAccessResponse?.accessResponse !== AccessResponse.ACCESS_GRANTED;
  };

  const shouldNotDisplay = (): boolean => !shouldDisplay();

  const getWrapperClassName = () => {
    const classes = [];
    if (shouldNotDisplay()) classes.push('single-hidden');
    if (maxWidth) classes.push('page-width');
    return classes.join(' ');
  };

  if (shouldNotDisplay()) return null;

  return (
    <section class="single-promoted-products-wrapper" style={cssVars}>
      <div class={`single-promoted-products ${getWrapperClassName()}`}>
        {!loading && (
          <section class="single-products-wrapper">
            {products && products?.length > 0 && (
              <>
                {(titleText || description) && (
                  <section class="single-products-header">
                    {titleText && <h2 class={`single-title ${titleSize || ''}`}>{titleText}</h2>}

                    {description && (
                      <div
                        class="single-description"
                        dangerouslySetInnerHTML={{ __html: description }}
                      />
                    )}
                  </section>
                )}

                <section class="single-card-grid">
                  {products?.map((product, i) => (
                    <ProductCard
                      key={product.id || i}
                      product={product}
                      productTitleSize={productTitleSize}
                      showProductTitle={showProductTitle}
                      showProductPrice={showProductPrice}
                      showQuickAdd={showQuickAdd}
                      separateTab={separateTab}
                    />
                  ))}
                </section>
              </>
            )}
          </section>
        )}
      </div>
    </section>
  );
};

export const PromotedProductsComponent: FunctionComponent<PromotedProductsComponentProps> = (
  props
) => {
  return (
    <Provider store={store}>
      <PromotedProductsContent {...props} />
    </Provider>
  );
};
